import React, { useEffect, useState } from "react";

import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import {
	fetchRSOById,
	fetchSpaceOrgs,
	markRSOAsDeletedByIds,
	selectDeletingUserRSO,
	selectRSOsById,
	updateUserRSOCospar,
	updateUserRSOName,
} from "./slice";
import {
	clearMatches,
	fetchObservations,
	selectObservationsByContext,
	selectObservationsByIds,
} from "../observations/slice";
import NavMain from "../../app/NavMain";
import Layout from "../../ui/Layout";
import ScrollablePage from "../../ui/ScrollablePage";
import Paper from "../../ui/Paper";
import RSOType from "./RSOType";
import ObservationsTable from "../observations/ObservationsTable";
import PropTypes from "prop-types";
import SpaceOrganisation from "./SpaceOrganisation";
import { calculateOrbitType } from "./orbit";
import styled from "styled-components";
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";
import ScheduledObservationsTable from "../scheduled-observations/ScheduledObservationsTable";
import {
	fetchScheduledObservations,
	selectScheduledObservationsByContext,
	selectScheduledObservationsByIds,
} from "../scheduled-observations/slice";
import { DateTime } from "luxon";
import PaperTitle, { PaperFooter, TitleText } from "../../ui/PaperTitle";
import {
	fetchPasses,
	selectPassesByContext,
	selectPassesByIds,
} from "../passes/slice";
import PassesTable from "../passes/PassesTable";
import Button from "../../ui/Button";
import { Action, Actions, Facts, Header, Overview } from "../../ui/PageHeader";
import Breadcrumbs from "../../ui/Breadcrumbs";
import IDCopy from "../../ui/IDCopy";
import ErrorMsg from "../../ui/ErrorMsg";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
	faClipboardCheck,
	faCopy,
	faDownload,
	faGlobe,
} from "@fortawesome/free-solid-svg-icons";

import { API_BASE_URL } from "../../app/config";
import GeneratePasses from "../passes/GeneratePasses";
import Pill from "../../ui/Pill";
import DeleteConfirmationDialog from "../../ui/DeleteConfirmationDialog";
import AgeSince from "./AgeSince";
import UpdateTLE from "./UpdateTLE";
import UserEditable from "../../ui/UserEditable";
import RSOBrightness from "./RSOBrightness";

const OBS_LIMIT = 10;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding-top: 1rem;
`;
const RSOTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  h2 {
    flex: auto;
  }
  div {
    margin-right: 1rem;
    flex: none;
  }
`;

const RSO = () => {
	const dispatch = useDispatch();

	const rsosByID = useSelector(selectRSOsById);
	const { id } = useParams();
	const [isRegeneratingPasses, setIsRegeneratingPasses] = useState(false);
	const [isUpdatingTLE, setIsUpdatingTLE] = useState(false);

	const deleting = useSelector(selectDeletingUserRSO);
	// we get any matching observations for this RSO
	const observationMatches = useSelector((state) =>
		selectObservationsByContext(state, `rso/${id}`),
	);
	// We get the full objects from the ids the match
	const observations = useSelector((state) =>
		selectObservationsByIds(state, observationMatches?.ids || []),
	);

	// we get any matching data passes
	const passesMatches = useSelector((state) =>
		selectPassesByContext(state, `rso/${id}`),
	);
	// We get the full objects from the ids that match
	const passes = useSelector((state) =>
		selectPassesByIds(state, passesMatches?.ids || []),
	);

	const rso = rsosByID[id];
	const status = rso && rso._loadStatus;

	const observationsFilter = {
		object_id: id,
	};

	const [isAlertVisible, setIsAlertVisible] = useState(false);

	const handleButtonClick = (e) => {
		e.stopPropagation();
		setIsAlertVisible(true);
		setTimeout(() => {
			setIsAlertVisible(false);
		}, 3000);
	};

	const currentDate = new Date(); // Get the current datetime
	const futureDate = new Date(currentDate.getTime()); // Create a copy of the current datetime
	futureDate.setUTCDate(futureDate.getUTCDate() + 20); // Add 10 days to the future date in UTC

	const handleRegenerateComplete = () => {
		dispatch(
			fetchPasses({
				filter: {
					object_ids: [id],
					from: currentDate.toISOString(),
					to: futureDate.toISOString(),
					sort_by: "start_date",
					sort_order: "ASC",
				},
				page: {
					offset: 0,
					limit: 20,
				},
				context: `rso/${id}`,
			}),
		);
	};

	useEffect(() => {
		const currentDate = new Date(); // Get the current datetime
		const futureDate = new Date(currentDate.getTime()); // Create a copy of the current datetime
		futureDate.setUTCDate(futureDate.getUTCDate() + 30); // Add 30 days to the future date in UTC

		dispatch(fetchRSOById(id));
		dispatch(fetchSpaceOrgs());
		dispatch(clearMatches());
		dispatch(
			fetchObservations({
				filter: observationsFilter,
				page: {
					offset: 0,
					limit: OBS_LIMIT,
				},
				context: `rso/${id}`,
			}),
		);
		dispatch(
			fetchPasses({
				filter: {
					object_ids: [id],
					from: currentDate.toISOString(),
					to: futureDate.toISOString(),
					sort_by: "start_date",
					sort_order: "ASC",
				},
				page: {
					offset: 0,
					limit: 20,
				},
				context: `rso/${id}`,
			}),
		);
	}, [id]);

	const tleText = rso
		? `${rso.tle_line0}\n${rso.tle_line1}\n${rso.tle_line2}`
		: "";

	function downloadPassesCSVFile(passIds) {
		// Create a temporary link element
		const link = document.createElement("a");
		link.href = `${API_BASE_URL}/api/passes/data?pass_ids=${passIds}`;
		link.target = "_blank"; // Open the download in a new tab

		// Trigger the download
		link.click();
	}

	const handleOpenRegeneratePasses = () => {
		setIsRegeneratingPasses(!isRegeneratingPasses);
	};

	const handleCloseRegeneratePasses = () => {
		setIsRegeneratingPasses(false);
	};

	const handleOpenUpdateTLE = () => {
		setIsUpdatingTLE(!isUpdatingTLE);
	};

	const handleCloseUpdateTLE = () => {
		setIsUpdatingTLE(false);
	};

	// Close on ESC
	useEffect(() => {
		const handler = (e) => {
			if (e.key == "Escape") {
				setIsRegeneratingPasses(false);
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	});

	const onDelete = async () => {
		await dispatch(markRSOAsDeletedByIds([id]));
	};

	const onObjectNameChange = (object_name) => {
		// TODO: Upon failure, this does not Promise.reject / throw exception,
		// so UserEditable cannot show failure status. Discuss.
		return dispatch(
			updateUserRSOName({
				id: rso?.id,
				_etag: rso?._etag,
				object_name,
			}),
		);
	};

	const onObjectCosparChange = (object_id) => {
		return dispatch(
			updateUserRSOCospar({
				id: rso?.id,
				_etag: rso?._etag,
				cospar: object_id,
			}),
		);
	};

	return (
		<Layout>
			<NavMain />
			<Helmet>
				<title>RSO {id} - Astrosite</title>
			</Helmet>
			<ScrollablePage>
				{!rso?.object_name && status === "loading" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				{deleting?._saveStatus === "deleting" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				<Header>
					<Overview>
						{rso?.source === "user" ? (
							<div>
								<RSOType type={rso?.object_type} />
								<UserEditable
									tagName="h1"
									value={`${rso?.object_name}`}
									onChange={onObjectNameChange}
									disabled={status !== "idle"}
								/>
								{rso?.source === "user" && <Pill>custom</Pill>}
							</div>
						) : (
							<RSOTitle>
								<RSOType type={rso?.object_type} />
								<h2>
									{rso?.object_name}
									{rso?.source === "user" && <Pill>custom</Pill>}
								</h2>
							</RSOTitle>
						)}

						{rso?.source === "user" && <p>User-specified / custom TLE</p>}
						<Breadcrumbs>
							<Link to="/rsos">RSOs</Link>
							<IDCopy id={id} showCopy />
						</Breadcrumbs>
						{rso?.deleted && (
							<ErrorMsg
								showIcon={true}
								msg={"Warning object is marked as deleted"}
							/>
						)}
						{deleting?._saveStatus == "rejected" && (
							<ErrorMsg
								msg={"an error occurred deleting the RSO"}
								showIcon={true}
							/>
						)}
					</Overview>
					<Facts>
						<dt>Data Source</dt>
						<dd>{rso?.source}</dd>
						<dt>Norad ID</dt>
						<dd>
							<IDCopy id={rso?.norad_id} showCopy />
						</dd>
						<dt>COSPAR ID</dt>
						<dd>
							<IDCopy id={rso?.object_id} showCopy />
							{/* TODO: Why was this editable?
							<UserEditable
								tagName="p1"
								value={`${rso?.object_id}`}
								onChange={onObjectCosparChange}
								disabled={status !== "idle"}
							/>
							*/}
						</dd>
						<dt>Object Type</dt>
						<dd>
							<RSOType type={rso?.object_type} printName />
						</dd>
						<dt
							title={
								"Radar cross-section is a measure of how detectable an object is by radar. A larger RCS indicates that an object is more easily detected."
							}
						>
							RCS Size
						</dt>
						<dd>{rso?.rcs_size}</dd>
						<dt>Launch date</dt>
						<dd>{rso?.launch_date}</dd>
						<dt>Launch site</dt>
						<dd>{rso?.site}</dd>
						<dt>Launched by</dt>
						<dd>
							{rso?.country_code ? (
								<SpaceOrganisation code={rso?.country_code} />
							) : (
								"unknown"
							)}
						</dd>
					</Facts>
					<Facts>
						<dt>Brightness</dt>
						<dd>
							<RSOBrightness {...rso} />
						</dd>
						<dt>Orbit</dt>
						<dd>
							{rso &&
								calculateOrbitType(rso.periapsis, rso.apoapsis, rso.period)}
						</dd>
						<dt>Perigee</dt>
						<dd>{rso?.periapsis}</dd>
						<dt>Apogee</dt>
						<dd>{rso?.apoapsis} km</dd>
						<dt>Inclination</dt>
						<dd>{rso?.inclination}&#176;</dd>
						<dt>Period</dt>
						<dd>{rso?.period}</dd>
						<dt>Semi major axis</dt>
						<dd>{rso?.semimajor_axis}</dd>
						<dt>TLE epoch</dt>
						<dd>
							<AgeSince {...rso} />
						</dd>
					</Facts>
					<Actions>
						{rso?.source === "user" && (
							<Action>
								<Button onClick={handleOpenUpdateTLE} icon={faGlobe}>
									Update TLE
								</Button>
							</Action>
						)}
						<Action>
							<CopyToClipboard text={tleText}>
								<Button
									icon={isAlertVisible ? faClipboardCheck : faCopy}
									onClick={handleButtonClick}
								>
									Copy latest TLE
								</Button>
							</CopyToClipboard>
						</Action>
						<Action>
							<Button onClick={handleOpenRegeneratePasses} icon={faGlobe}>
								Regenerate passes
							</Button>
						</Action>
						<Action>
							<Button
								icon={faDownload}
								onClick={() => downloadPassesCSVFile(passes?.map((p) => p.id))}
							>
								Export {passes?.length} passes as CSV
							</Button>
						</Action>
						{rso?.source === "user" && !rso?.deleted && (
							<Action>
								<DeleteConfirmationDialog
									text="Delete RSO"
									item={{ name: rso?.object_name }}
									onDelete={() => onDelete(rso?.id)}
								/>
							</Action>
						)}
					</Actions>
				</Header>
				{status === "rejected" && (
					<ErrorMsg
						msg={`There was a problem fetching the RSO with ID ${id}`}
						showIcon={true}
					/>
				)}
				<Container>
					<RSOPasses
						passes={passes}
						rsoID={id}
						status={passesMatches?._loadStatus}
						pageInfo={passesMatches?.page}
						from={currentDate.toISOString()}
						to={futureDate.toISOString()}
					/>
					<RSOObservations
						rsoID={id}
						observations={observations}
						observationsStatus={observationMatches?._loadStatus}
						observationsPageInfo={observationMatches?.page}
					/>
				</Container>
			</ScrollablePage>
			{isRegeneratingPasses && (
				<GeneratePasses
					handleCancel={handleCloseRegeneratePasses}
					noradIds={[id]}
					handleComplete={handleRegenerateComplete}
				/>
			)}
			{isUpdatingTLE && (
				<UpdateTLE
					id={id}
					eTag={rso?._etag}
					tleLine0={rso?.tle_line0}
					tleLine1={rso?.tle_line1}
					tleLine2={rso?.tle_line2}
					handleCancel={handleCloseUpdateTLE}
				/>
			)}
		</Layout>
	);
};

const RSOScheduledObservations = ({ rsoId }) => {
	const dispatch = useDispatch();

	// we get any matching collections for this Observation
	const scheduledObservationMatches = useSelector((state) =>
		selectScheduledObservationsByContext(state, `rso/${rsoId}`),
	);
	// We get the full objects from the ids the match
	const scheduledObservations = useSelector((state) =>
		selectScheduledObservationsByIds(
			state,
			scheduledObservationMatches?.ids || [],
		),
	);

	useEffect(() => {
		dispatch(
			fetchScheduledObservations({
				filter: {
					object_id: rsoId,
					from: DateTime.now().toUTC().toISO(),
					to: DateTime.now().plus({ weeks: 1 }).toUTC().toISO(),
				},
				page: {
					offset: 0,
					limit: 1000,
				},
				context: `rso/${rsoId}`,
			}),
		);
	}, [rsoId]);

	return (
		<Paper>
			<PaperTitle>
				<TitleText
					loadingState={scheduledObservationMatches?._loadStatus || "idle"}
				>
					Upcoming scheduled observations
				</TitleText>
			</PaperTitle>

			{scheduledObservationMatches?._loadStatus == "rejected" && (
				<div>Problem loading the matching scheduled observations</div>
			)}
			<ScheduledObservationsTable
				scheduledObservations={scheduledObservations}
				stickyHeader={false}
				hideFilters={true}
			/>
		</Paper>
	);
};

RSOScheduledObservations.propTypes = { rsoId: PropTypes.string };

// Renders an table of the observations that the relevant rso is in (no filters)
const RSOObservations = ({
	rsoID,
	observations = [],
	observationsStatus = "idle",
	observationsPageInfo = {},
}) => {
	return (
		<Paper>
			<PaperTitle>
				<TitleText loadingState={observationsStatus || "idle"}>
					Recent observations
				</TitleText>
			</PaperTitle>

			{observationsStatus == "rejected" && (
				<div>Problem loading the matching observations</div>
			)}
			<ObservationsTable
				observations={observations}
				filter={{
					// Not needed, but powers list navigation in the observation detail view
					object_id: rsoID,
				}}
				hideFilters={true}
				hideObject={true}
			/>
			{observations.length != 0 &&
				observationsPageInfo?.total_count > observations?.length && (
					<PaperFooter>
						<span>
							showing {observations?.length} of{" "}
							{observationsPageInfo?.total_count}{" "}
							<Link to={`/observations?object_id=${rsoID}`}>view all</Link>
						</span>
					</PaperFooter>
				)}
		</Paper>
	);
};

RSOObservations.propTypes = {
	rsoID: PropTypes.string.isRequired,
	observations: PropTypes.array,
	observationsStatus: PropTypes.string,
	observationsPageInfo: PropTypes.object,
};

// Renders a table of passes for this RSO
const RSOPasses = ({
	passes = [],
	rsoID,
	status = "idle",
	pageInfo = {},
	from,
	to,
}) => {
	return (
		<Paper>
			<PaperTitle>
				<TitleText loadingState={status || "idle"}>Upcoming passes</TitleText>
			</PaperTitle>

			{status == "rejected" && (
				<ErrorMsg msg={"Problem loading the matching passes"} showIcon={true} />
			)}
			<PassesTable passes={passes} hideFilters={true} hideObject={true} />
			{passes.length != 0 && pageInfo?.total_count > passes.length && (
				<footer>
					showing {passes.length} of {pageInfo?.total_count}
					<Link
						to={`/passes?norad_ids=${rsoID}&from=${from}&to=${to}&sort_by=start_date&sort_order=ASC`}
					>
						{" "}
						view all
					</Link>
				</footer>
			)}
		</Paper>
	);
};

RSOPasses.propTypes = {
	rsoID: PropTypes.string,
	passes: PropTypes.array,
	status: PropTypes.string,
	pageInfo: PropTypes.object,
	from: PropTypes.string,
	to: PropTypes.string,
};

export default RSO;
